/**
 * Dead vs. transient — Severino's review, finding 11c, the third honest
 * state Task 12 exists to add.
 *
 * "A network hiccup is not a dead venn" (the plan's own words): a
 * definitive denial (revoked, epoch-killed, never-existed — all the same
 * generic shape by design, per Task 8) settles the stage CLOSED. Every
 * other failure — the fetch itself failing, timing out, a WAF or proxy
 * or ITP getting in the way, the server having its own hiccup — means
 * the resolve/read round-trip simply hasn't succeeded YET, and is
 * retried with the same in-memory secret (hashSecret.ts), shown as a
 * visibly different, temporary state.
 *
 * THE RECOGNIZED SHAPE IS NOW THE REAL ONE (Ferruccio's gate, PR #83,
 * B1, blocking): the previous draft here NAMED a shape (`403` + JSON
 * body `{denied:true}`) that never existed on the wire — his probe
 * pushed the merged `workers/src/denial.ts`'s own `genericDenial()`
 * straight through this classifier and got `'transient'` back, which
 * means CLOSED was UNREACHABLE in production: every revoked, epoch-
 * killed, never-existed, or wrong-speaker denial would have shown
 * "still finding its way to you…" forever. `genericDenial()` is real
 * now (Task 7's resolve.ts and Task 8's read.ts both merged) and its
 * shape is exactly `401` + JSON body `{ ok: false }` — that is what
 * `isGenericDenial` below checks, confirmed against
 * `workers/src/denial.ts`'s own source, not assumed.
 *
 * THE INVERSION'S DIRECTION STAYS RIGHT (his own concession): the
 * question is still "did the SERVER tell us, in the ONE shape it uses
 * for every definitive denial, that this is dead" rather than "which
 * numeric range is this" — a bare status check would have re-opened the
 * exact 401/403/407/421/status-0 hole Severino's ride already closed.
 * Only the recognized shape's LITERAL VALUE was wrong; the fix is one
 * function.
 *
 * A 401 IS ALSO WHAT A MISSING/EXPIRED CREDENTIAL RETURNS, on BOTH the
 * routes this classifier ever sees a denial from — the router's own
 * dispatcher answers a missing/bad `Authorization: Bearer` header with
 * the SAME `genericDenial()` a resolve.ts-level "no such token, revoked,
 * epoch-killed, or a different-device replay" denial gets (see
 * router.ts's `case 'device-auth':` and `case 'session-auth':`), by the
 * same "no discovery of any kind" design this whole plan already uses
 * elsewhere. `classifyFailure` alone therefore CANNOT tell "the link is
 * dead" apart from "my own device/session credential is the problem" —
 * that distinction is not recoverable from the wire shape at all, on
 * purpose. It is recoverable from CONTEXT, which only the caller has:
 * `arrival.ts` treats a resolve-time denial as ambiguous (it re-enrolls
 * a FRESH device credential — free, always available — and retries
 * ONCE before accepting "dead", ruling out "it's just my stale secret"
 * first) but treats a read-time denial as unambiguous (Fork 6's own
 * ruling: sessions carry no TTL, so a session that once worked and now
 * doesn't has been REVOKED or epoch-killed, not merely aged out — and no
 * fresh device credential can rescue a revoked GRANT, so there is no
 * analogous ambiguity to resolve there). See arrival.ts's own
 * `beginResolve` for exactly where that split is made — this file only
 * ever answers "is this the recognized denial shape", never "which
 * call was this."
 */

export type ResolveSignal =
  /** `fetch` itself rejected (offline, DNS failure) or api.ts's own
   *  request timeout fired — no definitive server verdict was ever
   *  received. */
  | { readonly kind: 'network-error' }
  /** A response came back, whatever its status — success is filtered
   *  out by the caller before this classifier ever runs, so every
   *  signal reaching here is already known to be a failure of some
   *  kind. `body` is the best-effort parsed JSON body (`null` if the
   *  response had none, or wasn't JSON — a WAF or proxy's own error
   *  page is not JSON, and that is itself informative: it did not come
   *  from `workers/`). */
  | {
      readonly kind: 'http-response';
      readonly status: number;
      readonly body: unknown;
    };

export type FailureKind = 'dead' | 'transient';

/** Statuses that are never a verdict on the LINK, only on the REQUEST —
 *  "the server is overloaded" (5xx), "you're going too fast" (429),
 *  "slow down and retry" (408, 425). Checked BEFORE the denial-shape
 *  test below so a coincidental 401 body shaped like `{ok:false}` riding
 *  on one of these (shouldn't happen from the real server, but this
 *  classifier doesn't get to assume its own caller's honesty) can never
 *  masquerade as the recognized denial. */
function isRequestLevelHiccup(status: number): boolean {
  if (status >= 500) return true;
  return status === 408 || status === 425 || status === 429;
}

/** The ONE wire shape `workers/src/denial.ts`'s `genericDenial()`
 *  actually produces, confirmed against its source (not assumed):
 *  `401` with a JSON body of exactly `{ ok: false }`. Every definitive
 *  denial in the merged server — a bad/expired/missing device secret, a
 *  bad/revoked/epoch-killed bearer link token, a missing/revoked/epoch-
 *  killed session cookie, `canRead` denying for any reason — reaches
 *  this exact shape, by design (the router's own dispatcher and both
 *  Task 7/8 handlers all call the same function). Exported so
 *  `arrival.ts` can ask "is this THE recognized shape" directly, on the
 *  one route (resolve) where that alone is not the whole answer — see
 *  the module comment above. */
export function isGenericDenial(signal: ResolveSignal): boolean {
  if (signal.kind !== 'http-response') return false;
  if (isRequestLevelHiccup(signal.status)) return false;
  if (signal.status !== 401) return false;
  const body = signal.body;
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as Record<string, unknown>).ok === false
  );
}

/** Classifies one failed resolve/read/enroll attempt in isolation — pure,
 *  no fetch, no DOM, no clock, no notion of "which call was this" or
 *  "has a fresh credential already been tried". Defaults to 'transient'
 *  for anything it does not positively recognize as the one denial
 *  shape — see the module comment for why that default direction is the
 *  honest one, and see arrival.ts for the context-dependent handling a
 *  resolve-time denial specifically needs on top of this. */
export function classifyFailure(signal: ResolveSignal): FailureKind {
  if (signal.kind === 'network-error') return 'transient';
  if (isRequestLevelHiccup(signal.status)) return 'transient';
  return isGenericDenial(signal) ? 'dead' : 'transient';
}
